import React, { useState, useEffect } from 'react'
import Link from 'next/link'
import clsx from 'clsx'
import queryString from 'query-string'
import { useRouter } from 'next/router'

import { Text } from '@wartek-id/text'
import { useToast } from '@wartek-id/toast'
import { Icon } from '@wartek-id/icon'

import { isActivityComplete } from 'app/PelatihanMandiriPage/helper'
import ActivityItem from './ActivityItem'
import Checkmark from 'app/PelatihanMandiriPage/components/CheckmarkNoFill'
import { GATrackEvent } from 'utils/googleAnalytics'
import { usePelatihanMandiriDesktopDetailView } from 'app/PelatihanMandiriPage/hooks/usePelatihanMandiriDesktopDetailView'

import type { MaterialDataResponse } from 'types/microlearning'
import type { ParsedQuery } from 'query-string'
import type { MateriContent, ResponseQuiz } from 'types/microlearning'

import ArrowUpIcon from 'assets/icons/arrow-alt-up.svg'

export interface MateriCardProps {
  query: ParsedQuery
  id: string
  name: string
  onClicked: (e: React.MouseEvent<HTMLDivElement>) => void
  contents: MateriContent[]
  quiz?: ResponseQuiz
  reflective?: MaterialDataResponse['reflective']
  isComplete?: boolean
  isShouldRetry?: boolean
  isExpanded?: boolean
}

const MateriCard = React.forwardRef<HTMLDivElement | null, MateriCardProps>(
  (
    {
      query,
      id,
      name,
      quiz,
      reflective,
      isComplete = false,
      isShouldRetry = false,
      isExpanded = false,
      onClicked = (e) => {
        e.preventDefault()
      },
      contents,
    },
    ref
  ) => {
    const isDesktopScreenSize = usePelatihanMandiriDesktopDetailView()
    const router = useRouter()
    const { id: selectedMateriId } = router.query
    const baseUrl =
      process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH || 'pelatihan-mandiri'

    const toast = useToast()
    const [isContentComplete, setIsContentComplete] = useState<boolean>(
      isActivityComplete(contents, quiz)
    )

    useEffect(() => {
      setIsContentComplete(isActivityComplete(contents, quiz))
    }, [contents, quiz])

    const getRecordProgressState = (content) => {
      const TYPE_DETECTION = ['video', 'text']
      const PROGRESS_FLAG = ['COMPLETED']

      const isProgressShouldRecord = TYPE_DETECTION.includes(content.type)
      const isTypeShouldRecord = PROGRESS_FLAG.includes(content.progress)

      return !(isProgressShouldRecord && isTypeShouldRecord)
    }

    // we have to pre-build the quiz and reflective url since quiz has no id
    const quizUrl =
      `/${baseUrl}/quiz/${id}?` +
      queryString.stringify({
        topik: query.topik,
        modul: query.modul,
        topik_name: query.topik_name,
        modul_name: query.modul_name ?? '',
        materi_name: name,
        from: query.from,
        menuId: query.menuId,
        materi: id,
        return_view: query.return_view,
      })

    const reflectiveUrl =
      `/${baseUrl}/refleksi/${id}?` +
      queryString.stringify({
        topik: query.topik,
        modul: query.modul,
        topik_name: query.topik_name,
        modul_name: query.modul_name ?? '',
        materi_name: name,
        from: query.from,
        menuId: query.menuId,
        materi: id,
        return_view: query.return_view,
      })

    const isReflectiveActive =
      router.pathname === '/pelatihan-mandiri/refleksi/[id]' &&
      !!reflective &&
      (selectedMateriId as string) === String(id)

    const isQuizActive =
      router.pathname === '/pelatihan-mandiri/quiz/[id]' &&
      (selectedMateriId as string) === String(id)

    return (
      <div
        className="px-3 mb-3 bg-white rounded-lg border border-subdued"
        ref={ref}
      >
        <div
          data-testid="materi-card"
          onClick={onClicked}
          className={clsx(
            'relative flex -mx-3 px-3 py-4 hover:cursor-pointer',
            isExpanded && 'border-b border-subdued'
          )}
        >
          <div className="flex-shrink-0 flex-grow-0 w-6 h-6 bg-white rounded-full z-10">
            <Checkmark
              className={clsx(
                !isComplete && 'text-icon-disabled',
                isComplete && isShouldRetry && 'text-warning',
                isComplete && !isShouldRetry && 'text-success'
              )}
              data-testid={
                !isComplete
                  ? 'materi_not_completed'
                  : isShouldRetry
                  ? 'materi_should_retry'
                  : 'materi_completed'
              }
            />
          </div>
          <div className="flex-grow ml-3 pr-6">
            <Text variant="heading-sm">{name}</Text>
            {isShouldRetry && (
              <Text
                variant="helper-bold"
                color="warning"
                style={{
                  color: 'var(--color-warning)',
                }}
              >
                Perlu dipelajari ulang
              </Text>
            )}
          </div>
          <div className="absolute top-0 right-0 flex items-center justify-center w-10 h-full focus:outline-none">
            <ArrowUpIcon
              className={clsx(isExpanded === false && 'transform rotate-180')}
            />
          </div>
        </div>
        <div
          className={clsx(
            isExpanded === false && 'max-h-0 overflow-hidden',
            isExpanded && 'py-4'
          )}
        >
          <div className="sub-item pb-6">
            <div className="mb-3">
              <Text
                variant="subheading"
                color="subdued"
                className="uppercase tracking-wide"
              >
                Aktivitas
              </Text>
            </div>
            <div>
              {contents.map((content) => {
                const materialUrl =
                  `/${baseUrl}/${content.type}/${content.id}?` +
                  queryString.stringify({
                    modul: query.modul,
                    topik: query.topik,
                    materi: id,
                    topik_name: query.topik_name,
                    modul_name: query.modul_name ?? '',
                    materi_name: name,
                    from: query.from,
                    menuId: query.menuId,
                    return_view: query.return_view,
                    recordProgress: getRecordProgressState(content),
                  })
                return (
                  <ActivityItem
                    key={content.id}
                    name={content.name}
                    type={content.type}
                    href={materialUrl}
                    isComplete={
                      content.completed ||
                      (!!content.progress && content.progress === 'COMPLETED')
                    }
                    isShouldRetry={
                      (!!content.progress &&
                        content.progress === 'SHOULD_RETRY') ||
                      (!content.hasOwnProperty('progress') && isShouldRetry)
                    }
                    trackingParams={{
                      materi_id: id,
                      topik_id: query.topik as string,
                      module_id: query.modul as string,
                    }}
                    isActive={
                      (selectedMateriId as string) === String(content.id)
                    }
                  />
                )
              })}
              {!!quiz?.questions && quiz?.questions?.length > 0 && (
                <ActivityItem
                  name="Latihan Pemahaman"
                  type="quiz"
                  href={quizUrl}
                  isComplete={quiz.completed ?? false}
                  isShouldRetry={
                    (!!quiz.progress && quiz.progress === 'SHOULD_RETRY') ||
                    (!quiz.progress && isShouldRetry)
                  }
                  trackingParams={{
                    materi_id: id,
                    topik_id: query.topik as string,
                    module_id: query.modul as string,
                  }}
                  isActive={isQuizActive}
                />
              )}
            </div>
          </div>
          {!!reflective && (
            <div className="vertical-progress-indicator sub-item">
              <div className="w-full">
                <div className="mb-3">
                  <Text
                    variant="subheading"
                    color="subdued"
                    className="uppercase tracking-wide"
                  >
                    Cerita Reflektif
                  </Text>
                </div>
                <div
                  className={clsx(
                    isDesktopScreenSize &&
                      'flex items-center w-full gap-4 mb-3 last:mb-0',
                    isDesktopScreenSize &&
                      isReflectiveActive &&
                      'bg-[#0B5FEF] p-2 rounded-lg'
                  )}
                >
                  <div
                    className={clsx(
                      !isDesktopScreenSize
                        ? 'absolute top-12 left-1 w-6 h-4 bg-white rounded-full z-10'
                        : 'relative flex-shrink-0 flex-grow-0 flex items-center justify-center w-6 h-4 rounded-full z-10'
                    )}
                  >
                    <Checkmark
                      width={17}
                      height={16}
                      size="sm"
                      className={clsx(
                        !reflective?.completed && 'text-icon-disabled',
                        !!reflective?.completed && 'text-success',
                        isReflectiveActive && 'text-white'
                      )}
                      data-testid={
                        !!reflective?.completed
                          ? 'reflective_done'
                          : 'reflective_not_done'
                      }
                    />
                  </div>
                  <div
                    className={clsx(
                      'w-full',
                      !isDesktopScreenSize && 'pl-10',
                      isReflectiveActive && 'bg-[#0B5FEF] p-2 rounded-lg'
                    )}
                  >
                    {isContentComplete && !!reflective && (
                      <Link href={reflectiveUrl}>
                        {/* @next-codemod-error This Link previously used the now removed `legacyBehavior` prop, and has a child that might not be an anchor. The codemod bailed out of lifting the child props to the Link. Check that the child component does not render an anchor, and potentially move the props manually to Link. */}
                        {isDesktopScreenSize ? (
                          <div
                            className={clsx(
                              'flex items-center w-full gap-2 mb-3 last:mb-0'
                            )}
                          >
                            <div className={clsx('flex items-center')}>
                              <Icon
                                data-testid="materi_quiz"
                                color={
                                  isReflectiveActive ? 'inverse' : 'default'
                                }
                                as="i"
                                fontSize="sm"
                              >
                                person
                              </Icon>
                            </div>
                            <Text as="div" className="ml-2">
                              <a
                                href={reflectiveUrl}
                                className={clsx(
                                  'flex-grow font-semibold',
                                  isReflectiveActive
                                    ? 'text-white'
                                    : 'text-interactive-default'
                                )}
                              >
                                Tulis Cerita Anda
                              </a>
                            </Text>
                          </div>
                        ) : (
                          <a
                            data-testid="reflective-link"
                            onClick={() => {
                              GATrackEvent({
                                eventName: 'learning_activity',
                                eventParams: {
                                  event_trigger: 'click_refleksi_box',
                                  action_type: !reflective?.completed
                                    ? 'input_refleksi'
                                    : 'edit_refleksi',
                                  meta: JSON.stringify({
                                    materi_id: id,
                                    modul_id: query.modul,
                                    topik_id: query.topik,
                                  }),
                                },
                                sendDefaultParams: true,
                              })
                            }}
                            href={reflectiveUrl}
                            className={clsx(
                              'block p-3 w-full border rounded-lg truncate text-ellipsis',
                              isContentComplete
                                ? 'bg-white'
                                : 'bg-surface-disabled',
                              reflective.answer?.length > 0 && 'border-default',
                              reflective.answer?.length === 0 &&
                                'border-interactive-focused'
                            )}
                          >
                            <Text
                              as="span"
                              color={
                                reflective.answer?.length > 0
                                  ? 'default'
                                  : 'subdued'
                              }
                            >
                              {reflective.answer?.length > 0
                                ? reflective.answer
                                : 'Tulis refleksi Anda disini...'}
                            </Text>
                          </a>
                        )}
                      </Link>
                    )}
                    {!isContentComplete && (
                      <span
                        data-testid="reflective-inaccessible"
                        onClick={() => {
                          toast({
                            message: `Selesaikan semua aktivitas di materi ${name} terlebih dahulu`,
                            //status: 'error',
                          })
                        }}
                        className="block p-3 w-full border rounded-lg truncate text-ellipsis bg-surface-disabled border-disabled"
                      >
                        <Text as="span" color="disabled">
                          {reflective.answer.length > 0
                            ? reflective.answer
                            : 'Tulis refleksi Anda disini...'}
                        </Text>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }
)

MateriCard.displayName = 'MateriCard'
export default MateriCard
